import { ApiError, rawRequest } from "@aurumiq/api-client";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { env } from "@/lib/env";
import { AuthContext, type AuthContextValue } from "./auth-context";
import { type LocalSession, readSession, subscribe, writeSession } from "./local-session";
import { fetchSsoConfig, microsoftSignInUrl, type SsoConfig } from "./sso-config";

interface LoginResponse {
  token: string;
  expires_at: string;
  staff: { email: string; name: string; role: string };
}

async function postSession(path: string, body: unknown, invalidMsg: string): Promise<void> {
  try {
    const r = await rawRequest<LoginResponse>({ baseUrl: env.apiUrl }, path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    writeSession({
      token: r.token,
      expiresAt: r.expires_at,
      user: { name: r.staff.name, email: r.staff.email, role: r.staff.role },
    });
  } catch (e) {
    if (e instanceof ApiError) {
      const detail = (e.body as { error?: { message?: string } } | undefined)?.error?.message;
      if (e.status === 401) throw new Error(detail && path.includes("sso") ? detail : invalidMsg);
      if (e.status === 423) throw new Error("Too many failed attempts. Try again in 15 minutes.");
      if (e.status === 429) throw new Error("Too many attempts from this address. Slow down.");
      if (e.isNetwork) throw new Error("Cannot reach the API. Check your connection.");
    }
    throw new Error("Sign-in failed. Please try again.");
  }
}

/** POST /auth/login and persist the returned session. */
const loginWithPassword = (email: string, password: string) =>
  postSession("/api/v1/auth/login", { email, password }, "Invalid email or password.");

/** Swap the GoTrue token from the Microsoft redirect for a staff session. */
export const exchangeSsoToken = (accessToken: string) =>
  postSession(
    "/api/v1/auth/sso/exchange",
    { access_token: accessToken },
    "Microsoft sign-in was rejected.",
  );

function useLocalSession(): LocalSession | null {
  return useSyncExternalStore(subscribe, readSession, () => null);
}

/** Email + password sessions; also the fallback when Entra is not configured. */
function LocalAuthProvider({
  children,
  msalSignIn,
}: {
  children: ReactNode;
  msalSignIn?: () => Promise<void>;
}) {
  const session = useLocalSession();
  const signOut = useCallback(async () => writeSession(null), []);
  const value = useMemo<AuthContextValue>(
    () => ({
      mode: "local",
      ready: true,
      isAuthenticated: session !== null,
      user: session ? { name: session.user.name, email: session.user.email } : null,
      getToken: async () => readSession()?.token ?? null,
      signIn: msalSignIn,
      signInWithPassword: loginWithPassword,
      signOut,
    }),
    [session, msalSignIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** `VITE_AUTH_MODE=test`: no MSAL, every request carries `Bearer test:<role>`. */
function TestAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthContextValue>(
    () => ({
      mode: "test",
      ready: true,
      isAuthenticated: true,
      user: { name: `Test ${env.testRole}`, email: `${env.testRole}@test.local` },
      getToken: async () => `test:${env.testRole}`,
      signIn: undefined,
      signInWithPassword: async () => {},
      signOut: async () => {},
    }),
    [],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * SSO is configured at runtime (System → Single sign-on) and fetched once at boot. When it is on,
 * `signIn` sends the browser to GoTrue's Microsoft flow; the session itself is always the local
 * staff token, so nothing else in the panel cares which way the user arrived.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [sso, setSso] = useState<SsoConfig | null>(null);
  const isTest = env.authMode === "test";
  useEffect(() => {
    if (isTest) return;
    let cancelled = false;
    fetchSsoConfig().then((c) => {
      if (!cancelled) setSso(c);
    });
    return () => {
      cancelled = true;
    };
  }, [isTest]);
  const msalSignIn = useMemo(
    () =>
      sso?.enabled
        ? async () => {
            window.location.assign(microsoftSignInUrl(sso));
          }
        : undefined,
    [sso],
  );
  if (isTest) return <TestAuthProvider>{children}</TestAuthProvider>;
  if (!sso) return null;
  return <LocalAuthProvider msalSignIn={msalSignIn}>{children}</LocalAuthProvider>;
}
